import type { PoolActivityPageKind } from "@/domains/thick-v2/stores/pool-activity-store"
import type { ManualClPoolActivityRow } from "@/domains/validators"
import { Button } from "@/components/button"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn, dollarsPrettified } from "@/domains/library"

/**
 * Paginated activity feed for the pool detail page. Consumes one page of
 * rows at a time (swaps / mints / burns / collects) from
 * {@link PoolActivityStore} and delegates pagination to the store via the
 * `onNext` / `onPrev` callbacks.
 *
 * Pure view — does not touch the transport; tests can drive the full state
 * matrix by passing hand-rolled rows and asserting render output.
 */
export interface PoolActivityFeedViewProps {
  rows: Array<ManualClPoolActivityRow>
  activeKind: PoolActivityPageKind
  offset: number
  pageSize: number
  isLoading: boolean
  error: string | undefined
  token0Symbol: string
  token1Symbol: string
  onKindChange: (kind: PoolActivityPageKind) => void
  onNext: () => void
  onPrev: () => void
  className?: string
}

const KINDS: Array<PoolActivityPageKind> = ["Swaps", "Mints", "Burns", "Collects"]

export function PoolActivityFeedView({
  rows,
  activeKind,
  offset,
  pageSize,
  isLoading,
  error,
  token0Symbol,
  token1Symbol,
  onKindChange,
  onNext,
  onPrev,
  className,
}: PoolActivityFeedViewProps) {
  const hasRows = rows.length > 0
  const canGoPrev = offset > 0
  // We can't know the total without a count query; assume `pageSize` rows
  // means more are available. If the page returns fewer, the next page is
  // guaranteed empty — so disable next in that case.
  const canGoNext = hasRows && rows.length >= pageSize

  return (
    <section
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5",
        className,
      )}
      data-testid="pool-activity-feed"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <BodyXs className="font-medium uppercase tracking-wide text-t300">
          Activity
        </BodyXs>
        <nav
          className="inline-flex overflow-hidden rounded-md border border-p300-light bg-p300"
          aria-label="Activity kind"
        >
          {KINDS.map((kind) => {
            const selected = kind === activeKind
            return (
              <button
                key={kind}
                type="button"
                onClick={() => onKindChange(kind)}
                aria-pressed={selected}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  selected
                    ? "bg-p200 text-t100"
                    : "text-t200 hover:bg-p300-light",
                )}
              >
                {kind}
              </button>
            )
          })}
        </nav>
      </header>
      {error && (
        <div className="rounded-md border border-error-border/40 bg-error-background/10 px-3 py-2 text-xs text-error-text">
          {error}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-separate border-spacing-y-1 text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-t300">
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Value</th>
              <th className="px-3 py-2 font-medium">{token0Symbol}</th>
              <th className="px-3 py-2 font-medium">{token1Symbol}</th>
              <th className="px-3 py-2 font-medium">Tx</th>
            </tr>
          </thead>
          <tbody>
            {!hasRows && !isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-xs text-t300">
                  No {activeKind.toLowerCase()} on this page.
                </td>
              </tr>
            )}
            {hasRows &&
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="rounded-md bg-p300 text-t100"
                  data-testid="pool-activity-row"
                >
                  <td className="rounded-l-md px-3 py-2 text-xs text-t200">
                    {formatRelativeTime(row.timestamp)}
                  </td>
                  <td className="px-3 py-2 text-sm font-medium text-t100">
                    {row.amountUSD !== undefined && Number.isFinite(row.amountUSD)
                      ? dollarsPrettified(row.amountUSD)
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-t200">
                    {formatSignedAmount(row.amount0)}
                  </td>
                  <td className="px-3 py-2 text-xs text-t200">
                    {formatSignedAmount(row.amount1)}
                  </td>
                  <td className="rounded-r-md px-3 py-2 text-xs text-t100">
                    {(() => {
                      const hash = resolveTxHash(row.transaction)
                      if (!hash) return "—"
                      return (
                        <a
                          className="text-primary-400 hover:underline"
                          href={`https://basescan.org/tx/${hash}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {shortHash(hash)}
                        </a>
                      )
                    })()}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <footer className="flex items-center justify-between text-xs text-t300">
        <span>
          Page {Math.floor(offset / pageSize) + 1}
          {isLoading ? " · Loading…" : ""}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            disabled={!canGoPrev || isLoading}
            onClick={onPrev}
          >
            Prev
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={!canGoNext || isLoading}
            onClick={onNext}
          >
            Next
          </Button>
        </div>
      </footer>
    </section>
  )
}

function formatRelativeTime(unix: number): string {
  const nowSeconds = Math.floor(Date.now() / 1000)
  const delta = Math.max(0, nowSeconds - unix)
  if (delta < 60) return `${delta}s ago`
  if (delta < 3_600) return `${Math.floor(delta / 60)}m ago`
  if (delta < 86_400) return `${Math.floor(delta / 3_600)}h ago`
  return `${Math.floor(delta / 86_400)}d ago`
}

function formatSignedAmount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—"
  if (value === 0) return "0"
  const abs = Math.abs(value)
  const digits = abs >= 1 ? 4 : 6
  const sign = value > 0 ? "+" : "-"
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: digits })}`
}

function shortHash(hash: string): string {
  if (hash.length <= 10) return hash
  return `${hash.slice(0, 6)}…${hash.slice(-4)}`
}

/**
 * The indexer returns two flavours of `transaction`: the raw
 * `transactionHash` field, and a composite `<hash>-<chainId>` id we use as
 * primary key. We prefer the explicit hash and fall back to stripping the
 * chain suffix off the id so older snapshots keep working.
 */
function resolveTxHash(transaction: ManualClPoolActivityRow["transaction"]): string | undefined {
  if (!transaction) return undefined
  if (transaction.transactionHash && transaction.transactionHash.startsWith("0x")) {
    return transaction.transactionHash
  }
  const id = transaction.id ?? ""
  const lastDash = id.lastIndexOf("-")
  if (id.startsWith("0x") && lastDash > 0) return id.slice(0, lastDash)
  return id || undefined
}
